import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useCreateReferral,
  type PPRDocuments,
  type HospitalDocuments,
  type OtherDocuments,
  type DocumentsToSend,
  type AssessmentSchemes,
  type PaperAnamneseForms,
  type SchoolCompletionForms,
  ASSESSMENT_SCHEME_ITEMS,
  applyAssessmentToggle,
  assessmentSchemeItemKey,
  isAssessmentParentOn,
  isAssessmentRespondentOn,
} from '@/hooks/useReferrals';
import { ApiCallError } from '@/lib/api';
import { isAcceptableCpr } from '@/lib/cpr';
import { Button } from '@/components/ui/Button';
import { DanishDateTimeField } from '@/components/ui/DanishDateTimeField';
import { PageLayout } from '@/components/ui/PageLayout';
import { Input } from '@/components/ui/Input';
import { DynamicTextFields } from '@/components/ui/DynamicTextFields';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { fromDatetimeLocal } from '@/lib/datetimeLocal';

export function NewReferral() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const create = useCreateReferral();

  const [referrerName, setReferrerName] = useState('');
  const [referrerType, setReferrerType] = useState<string>('');
  const [referrerReference, setReferrerReference] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cpr, setCpr] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [senderHospital, setSenderHospital] = useState('');
  const [senderDepartment, setSenderDepartment] = useState('');
  const [sentAt, setSentAt] = useState('');
  const [referringDoctorName, setReferringDoctorName] = useState('');
  const [referringDoctorSor, setReferringDoctorSor] = useState('');
  const [parent1Name, setParent1Name] = useState('');
  const [parent1Cpr, setParent1Cpr] = useState('');
  const [parent2Name, setParent2Name] = useState('');
  const [parent2Cpr, setParent2Cpr] = useState('');
  const [referralDiagnosis, setReferralDiagnosis] = useState('');
  const [ambulant, setAmbulant] = useState<'' | 'true' | 'false'>('');
  const [requestedExamination, setRequestedExamination] = useState('');
  const [anamnese, setAnamnese] = useState('');

  // New administrative fields
  const [secretaryName, setSecretaryName] = useState('');
  const [psychologistName, setPsychologistName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [caseNumber, setCaseNumber] = useState('');

  // PPR Documents
  const [pprDocs, setPprDocs] = useState<PPRDocuments>({});
  
  // Hospital Documents
  const [hospitalDocs, setHospitalDocs] = useState<HospitalDocuments>({});
  
  // Other Documents
  const [otherDocs, setOtherDocs] = useState<OtherDocuments>({});
  
  // Documents to Send
  const [docsToSend] = useState<DocumentsToSend>({});
  
  // Assessment Schemes
  const [assessmentSchemes, setAssessmentSchemes] = useState<AssessmentSchemes>({});

  // Paper Anamnese Forms
  const [paperAnamnese, setPaperAnamnese] = useState<PaperAnamneseForms>({});

  // School Completion Forms
  const [schoolCompletion, setSchoolCompletion] = useState<SchoolCompletionForms>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!activeClinicId) {
      setSubmitError(t('referrals.errors.no_clinic'));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t('referrals.errors.name_required'));
      return;
    }

    if (cpr.trim().length > 0 && !isAcceptableCpr(cpr)) {
      setSubmitError(t('referrals.errors.cpr_format'));
      return;
    }
    for (const value of [parent1Cpr, parent2Cpr]) {
      if (value.trim().length > 0 && !isAcceptableCpr(value)) {
        setSubmitError(t('referrals.errors.cpr_format'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const created = await create.mutateAsync({
        clinic_id: activeClinicId,
        referrer_name: referrerName.trim() || undefined,
        referrer_type: referrerType || undefined,
        referrer_reference: referrerReference.trim() || undefined,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        cpr: cpr.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        reason: reason.trim() || undefined,
        notes: notes.trim() || undefined,
        sender_hospital: senderHospital.trim() || undefined,
        sender_department: senderDepartment.trim() || undefined,
        sent_at: fromDatetimeLocal(sentAt) || undefined,
        referring_doctor_name: referringDoctorName.trim() || undefined,
        referring_doctor_sor: referringDoctorSor.trim() || undefined,
        parent_1_name: parent1Name.trim() || undefined,
        parent_1_cpr: parent1Cpr.trim() || undefined,
        parent_2_name: parent2Name.trim() || undefined,
        parent_2_cpr: parent2Cpr.trim() || undefined,
        referral_diagnosis: referralDiagnosis.trim() || undefined,
        ambulant: ambulant === 'true' ? true : ambulant === 'false' ? false : null,
        requested_examination: requestedExamination.trim() || undefined,
        anamnese: anamnese.trim() || undefined,
        secretary_name: secretaryName.trim() || undefined,
        psychologist_name: psychologistName.trim() || undefined,
        id_number: idNumber.trim() || undefined,
        case_number: caseNumber.trim() || undefined,
        ppr_documents: pprDocs,
        hospital_documents: hospitalDocs,
        other_documents: otherDocs,
        documents_to_send: docsToSend,
        assessment_schemes: assessmentSchemes,
        paper_anamnese_forms: paperAnamnese,
        school_completion_forms: schoolCompletion,
      });
      toast.success(
        t('referrals.toast_created', { name: `${created.first_name} ${created.last_name}` }),
      );
      navigate(`/henvisninger/${created.id}`);
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid-input') {
          setSubmitError(t('referrals.errors.invalid_input'));
        } else if (err.code === 'forbidden') {
          setSubmitError(t('referrals.errors.forbidden'));
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError(t('referrals.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout title={t('referrals.new_title')} subtitle={t('referrals.new_subtitle')}>
          <button
            type="button"
            onClick={() => navigate('/henvisninger')}
            className="mb-s4 inline-flex items-center text-ds-muted hover:text-ds-text"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('referrals.back_to_list')}
          </button>

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-6 rounded-lg border border-ds-line bg-ds-card p-6 shadow-card"
          >
            <Section title={t('referrals.section_referrer')}>
              <Grid>
                <Input
                  label={t('referrals.field_referrer_name')}
                  value={referrerName}
                  onChange={(e) => setReferrerName(e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_referrer_type')}
                  </label>
                  <select
                    value={referrerType}
                    onChange={(e) => setReferrerType(e.target.value)}
                    className="mt-1 block h-[44px] w-full rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                  >
                    <option value="">{t('referrals.referrer_type_none')}</option>
                    <option value="egen_laege">{t('referrals.referrer_type_egen_laege')}</option>
                    <option value="speciallaege">
                      {t('referrals.referrer_type_speciallaege')}
                    </option>
                    <option value="selvhenvist">
                      {t('referrals.referrer_type_selvhenvist')}
                    </option>
                    <option value="anden">{t('referrals.referrer_type_anden')}</option>
                  </select>
                </div>
                <Input
                  label={t('referrals.field_referrer_reference')}
                  value={referrerReference}
                  onChange={(e) => setReferrerReference(e.target.value)}
                  hint={t('referrals.field_referrer_reference_hint')}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_patient')}>
              <Grid>
                <Input
                  label={t('referrals.field_first_name')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <Input
                  label={t('referrals.field_last_name')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <Input
                  label={t('referrals.field_cpr')}
                  value={cpr}
                  onChange={(e) => setCpr(e.target.value)}
                  placeholder="1234567890"
                  hint={t('referrals.field_cpr_hint')}
                />
                <Input
                  label={t('referrals.field_phone')}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <Input
                  label={t('referrals.field_email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_sender')}>
              <Grid>
                <Input
                  label={t('referrals.field_sender_hospital')}
                  value={senderHospital}
                  onChange={(e) => setSenderHospital(e.target.value)}
                />
                <Input
                  label={t('referrals.field_sender_department')}
                  value={senderDepartment}
                  onChange={(e) => setSenderDepartment(e.target.value)}
                />
                <DanishDateTimeField
                  className="sm:col-span-2"
                  name="sent-at"
                  label={t('referrals.field_sent_at')}
                  value={sentAt}
                  onChange={setSentAt}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_referring_doctor')}>
              <Grid>
                <Input
                  label={t('referrals.field_referring_doctor_name')}
                  value={referringDoctorName}
                  onChange={(e) => setReferringDoctorName(e.target.value)}
                />
                <Input
                  label={t('referrals.field_referring_doctor_sor')}
                  value={referringDoctorSor}
                  onChange={(e) => setReferringDoctorSor(e.target.value)}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_parents')}>
              <Grid>
                <Input
                  label={t('referrals.field_parent_1_name')}
                  value={parent1Name}
                  onChange={(e) => setParent1Name(e.target.value)}
                />
                <Input
                  label={t('referrals.field_parent_1_cpr')}
                  value={parent1Cpr}
                  onChange={(e) => setParent1Cpr(e.target.value)}
                  placeholder="1234567890"
                  hint={t('referrals.field_cpr_hint')}
                />
                <Input
                  label={t('referrals.field_parent_2_name')}
                  value={parent2Name}
                  onChange={(e) => setParent2Name(e.target.value)}
                />
                <Input
                  label={t('referrals.field_parent_2_cpr')}
                  value={parent2Cpr}
                  onChange={(e) => setParent2Cpr(e.target.value)}
                  placeholder="1234567890"
                  hint={t('referrals.field_cpr_hint')}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_letter')}>
              <div className="space-y-4">
                <Input
                  label={t('referrals.field_referral_diagnosis')}
                  value={referralDiagnosis}
                  onChange={(e) => setReferralDiagnosis(e.target.value)}
                  placeholder={t('referrals.field_referral_diagnosis_placeholder')}
                />
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_ambulant')}
                  </label>
                  <select
                    value={ambulant}
                    onChange={(e) => setAmbulant(e.target.value as '' | 'true' | 'false')}
                    className="mt-1 block h-[44px] w-full rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:ring-ds-teal sm:max-w-xs"
                  >
                    <option value="">{t('referrals.ambulant_unknown')}</option>
                    <option value="true">{t('referrals.ambulant_yes')}</option>
                    <option value="false">{t('referrals.ambulant_no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_requested_examination')}
                  </label>
                  <textarea
                    rows={3}
                    value={requestedExamination}
                    onChange={(e) => setRequestedExamination(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    placeholder={t('referrals.field_requested_examination_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_anamnese')}
                  </label>
                  <textarea
                    rows={6}
                    value={anamnese}
                    onChange={(e) => setAnamnese(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    placeholder={t('referrals.field_anamnese_placeholder')}
                  />
                </div>
              </div>
            </Section>

            <Section title={t('referrals.section_details')}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_reason')}
                  </label>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    placeholder={t('referrals.field_reason_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('referrals.field_notes')}
                  </label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    placeholder={t('referrals.field_notes_placeholder')}
                  />
                </div>
              </div>
            </Section>

            <Section title="Administrative">
              <Grid>
                <Input
                  label="Sekretær"
                  value={secretaryName}
                  onChange={(e) => setSecretaryName(e.target.value)}
                />
                <Input
                  label="Psykolog"
                  value={psychologistName}
                  onChange={(e) => setPsychologistName(e.target.value)}
                />
                <Input
                  label="ID #"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                />
                <Input
                  label="Sagsnr."
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                />
              </Grid>
            </Section>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Anmodning om dokumenter fra PPR">
                <div className="space-y-3">
                  <Input
                    label="PPR"
                    value={pprDocs.institution_name ?? ''}
                    onChange={(e) => setPprDocs({ ...pprDocs, institution_name: e.target.value })}
                    placeholder="Angiv by, evt. også afdeling eller kontaktperson"
                  />
                  <CheckboxField
                    label="WISC V forside eller computerudskrift"
                    checked={pprDocs.wisc_v_forside ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wisc_v_forside: checked })}
                  />
                  <CheckboxField
                    label="WISC V rapport"
                    checked={pprDocs.wisc_v_rapport ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wisc_v_rapport: checked })}
                  />
                  <CheckboxField
                    label="WPPSI-IV forside"
                    checked={pprDocs.wppsi_iv_forside ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wppsi_iv_forside: checked })}
                  />
                  <CheckboxField
                    label="WPPSI-IV rapport"
                    checked={pprDocs.wppsi_iv_rapport ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wppsi_iv_rapport: checked })}
                  />
                  <CheckboxField
                    label="Andet"
                    checked={pprDocs.andet ?? false}
                    onChange={(checked) => setPprDocs({ 
                      ...pprDocs, 
                      andet: checked,
                      // Initialize andet_items with one empty field when checked
                      ...(checked ? { andet_items: [''] } : {})
                    })}
                  />
                  {pprDocs.andet && (
                    <div className="ml-6 mt-2">
                      <DynamicTextFields
                        values={pprDocs.andet_items ?? []}
                        onChange={(items) => setPprDocs({ ...pprDocs, andet_items: items })}
                        placeholder="Beskriv hvad der ønskes..."
                      />
                    </div>
                  )}
                </div>
              </Section>

              <Section title="Anmodning om dokumenter fra Hospital">
                <div className="space-y-3">
                  <Input
                    label="Hospital"
                    value={hospitalDocs.institution_name ?? ''}
                    onChange={(e) => setHospitalDocs({ ...hospitalDocs, institution_name: e.target.value })}
                    placeholder="Angiv hvilket, afdeling og årstal"
                  />
                  <CheckboxField
                    label="Journaludskrift"
                    checked={hospitalDocs.journaludskrift ?? false}
                    onChange={(checked) =>
                      setHospitalDocs({ ...hospitalDocs, journaludskrift: checked })
                    }
                  />
                  
                  {/* Expandable Scannings-svar section */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={hospitalDocs.scannings_svar ?? false}
                        onChange={(e) => setHospitalDocs({ ...hospitalDocs, scannings_svar: e.target.checked })}
                        className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                      />
                      <span className="text-sm font-medium text-ds-text">Scannings-svar</span>
                      <ChevronRight 
                        className={`h-4 w-4 text-ds-muted transition-transform duration-200 ease-in-out ${
                          hospitalDocs.scannings_svar ? 'rotate-90' : ''
                        }`}
                      />
                    </label>
                    
                    {/* Nested options with smooth transition */}
                    <div 
                      className={`
                        overflow-hidden transition-all duration-200 ease-in-out
                        ${hospitalDocs.scannings_svar ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}
                      `}
                    >
                      <div className="ml-6 pl-4 border-l-2 border-ds-line space-y-2">
                        <CheckboxField
                          label="MR"
                          checked={hospitalDocs.scannings_svar_mr ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ ...hospitalDocs, scannings_svar_mr: checked })
                          }
                        />
                        <CheckboxField
                          label="CT"
                          checked={hospitalDocs.scannings_svar_ct ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ ...hospitalDocs, scannings_svar_ct: checked })
                          }
                        />
                        <CheckboxField
                          label="Andet"
                          checked={hospitalDocs.scannings_svar_andet ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ 
                              ...hospitalDocs, 
                              scannings_svar_andet: checked,
                              // Initialize scannings_svar_andet_items with one empty field when checked
                              ...(checked ? { scannings_svar_andet_items: [''] } : {})
                            })
                          }
                        />
                        {hospitalDocs.scannings_svar_andet && (
                          <div className="mt-2 animate-in fade-in duration-200">
                            <DynamicTextFields
                              values={hospitalDocs.scannings_svar_andet_items ?? []}
                              onChange={(items) =>
                                setHospitalDocs({ ...hospitalDocs, scannings_svar_andet_items: items })
                              }
                              placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <CheckboxField
                    label="Andet"
                    checked={hospitalDocs.andet ?? false}
                    onChange={(checked) => setHospitalDocs({ 
                      ...hospitalDocs, 
                      andet: checked,
                      // Initialize andet_items with one empty field when checked
                      ...(checked ? { andet_items: [''] } : {})
                    })}
                  />
                  {hospitalDocs.andet && (
                    <div className="ml-6 mt-2">
                      <DynamicTextFields
                        values={hospitalDocs.andet_items ?? []}
                        onChange={(items) =>
                          setHospitalDocs({ ...hospitalDocs, andet_items: items })
                        }
                        placeholder="Beskriv hvad der ønskes..."
                      />
                    </div>
                  )}
                </div>
              </Section>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Anmodning om dokumenter fra Andre">
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    Ønskede dokumenter
                  </label>
                  <textarea
                    rows={3}
                    value={otherDocs.requested_items ?? ''}
                    onChange={(e) =>
                      setOtherDocs({ ...otherDocs, requested_items: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
                  />
                </div>
              </Section>
            </div>

            {/* Forms sent with invitation wrapper */}
            <div className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
                <h2 className="text-xl font-semibold text-ds-text mb-2">
                  Følgende medsendes indkaldelse
                </h2>
                <p className="text-sm text-ds-muted mb-6">
                  derudover sendes altid Anamnese, samtykkeerklæring hjem/skole/institution samt vejkort
                </p>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-6">
              <Section title="Papir Anamneseskema">
                <div className="space-y-2">
                  <CheckboxField
                    label="Småbørn"
                    checked={paperAnamnese.smaaboern ?? false}
                    onChange={(checked) => setPaperAnamnese({ ...paperAnamnese, smaaboern: checked })}
                  />
                  <CheckboxField
                    label="Småbørn genhenvisning"
                    checked={paperAnamnese.smaaboern_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, smaaboern_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Skolebørn"
                    checked={paperAnamnese.skoleboern ?? false}
                    onChange={(checked) => setPaperAnamnese({ ...paperAnamnese, skoleboern: checked })}
                  />
                  <CheckboxField
                    label="Skolebørn genhenvisning"
                    checked={paperAnamnese.skoleboern_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, skoleboern_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Unge 15+"
                    checked={paperAnamnese.unge_15_plus ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, unge_15_plus: checked })
                    }
                  />
                  <CheckboxField
                    label="Unge 15+ genhenvisning"
                    checked={paperAnamnese.unge_15_plus_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, unge_15_plus_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Tillæg til anamneseskema (13-18 år)"
                    checked={paperAnamnese.tillaeg_til_anamnese_13_18 ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, tillaeg_til_anamnese_13_18: checked })
                    }
                  />
                  <CheckboxField
                    label="Nordisk spørgeskema (2-5 år)"
                    checked={paperAnamnese.nordisk_sporgeskema_2_5 ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, nordisk_sporgeskema_2_5: checked })
                    }
                  />
                  <CheckboxField
                    label="DUPAUL forældre // skole"
                    checked={paperAnamnese.dupaul_foraeldre_skole ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, dupaul_foraeldre_skole: checked })
                    }
                  />
                </div>
              </Section>

              <Section title="Skema til udfyldelse af førskole/skole">
                <div className="space-y-2">
                  <CheckboxField
                    label="Førskole"
                    checked={schoolCompletion.førskole ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, førskole: checked })
                    }
                  />
                  <CheckboxField
                    label="Indskoling"
                    checked={schoolCompletion.indskoling ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, indskoling: checked })
                    }
                  />
                  <CheckboxField
                    label="Mellemtrin"
                    checked={schoolCompletion.mellemtrin ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, mellemtrin: checked })
                    }
                  />
                  <CheckboxField
                    label="Udskoling"
                    checked={schoolCompletion.udskoling ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, udskoling: checked })
                    }
                  />
                </div>
              </Section>
              </div>

              <Section title="Elektroniske skemaer (assessments)">
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2">
                    {ASSESSMENT_SCHEME_ITEMS.map((item) => {
                      switch (item.kind) {
                        case 'group': {
                          const group = item.group;
                          const parentOn = isAssessmentParentOn(assessmentSchemes, group);
                          return (
                            <div key={assessmentSchemeItemKey(item)}>
                              <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={parentOn}
                                  onChange={(e) =>
                                    setAssessmentSchemes(
                                      applyAssessmentToggle(
                                        assessmentSchemes,
                                        group.parent,
                                        e.target.checked,
                                      ),
                                    )
                                  }
                                  className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                                />
                                <span className="text-sm font-medium text-ds-text">
                                  {group.title}
                                </span>
                                <ChevronRight
                                  className={`h-4 w-4 text-ds-muted transition-transform duration-200 ease-in-out ${
                                    parentOn ? 'rotate-90' : ''
                                  }`}
                                />
                              </label>
                              <div
                                className={`
                              overflow-hidden transition-all duration-200 ease-in-out
                              ${parentOn ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}
                            `}
                              >
                                <div className="ml-6 pl-4 border-l-2 border-ds-line space-y-2">
                                  {group.respondents.map((respondent) => (
                                    <CheckboxField
                                      key={respondent.field}
                                      label={respondent.label}
                                      checked={isAssessmentRespondentOn(
                                        assessmentSchemes,
                                        respondent.field,
                                        group,
                                      )}
                                      onChange={(checked) =>
                                        setAssessmentSchemes(
                                          applyAssessmentToggle(
                                            assessmentSchemes,
                                            respondent.field,
                                            checked,
                                          ),
                                        )
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        case 'flag':
                          return (
                            <CheckboxField
                              key={assessmentSchemeItemKey(item)}
                              label={item.label}
                              checked={assessmentSchemes[item.field] ?? false}
                              onChange={(checked) =>
                                setAssessmentSchemes(
                                  applyAssessmentToggle(assessmentSchemes, item.field, checked),
                                )
                              }
                            />
                          );
                        case 'andet':
                          return (
                            <div key={assessmentSchemeItemKey(item)}>
                              <CheckboxField
                                label={item.label}
                                checked={assessmentSchemes.andet ?? false}
                                onChange={(checked) =>
                                  setAssessmentSchemes({
                                    ...assessmentSchemes,
                                    andet: checked,
                                    ...(checked ? { andet_items: [''] } : {}),
                                  })
                                }
                              />
                              {assessmentSchemes.andet && (
                                <div className="ml-6 mt-2">
                                  <DynamicTextFields
                                    values={assessmentSchemes.andet_items ?? []}
                                    onChange={(items) =>
                                      setAssessmentSchemes({
                                        ...assessmentSchemes,
                                        andet_items: items,
                                      })
                                    }
                                    placeholder="Angiv andre assessments..."
                                  />
                                </div>
                              )}
                            </div>
                          );
                        default: {
                          const _exhaustive: never = item;
                          return _exhaustive;
                        }
                      }
                    })}
                  </div>
                </div>
              </Section>
            </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-ds-line pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/henvisninger')}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={submitting || !activeClinicId}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('referrals.save_new')
                )}
              </Button>
            </div>
          </form>
    </PageLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-ds-text">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
      />
      <span className="text-sm text-ds-text">{label}</span>
    </label>
  );
}

